<script setup lang="ts">
import { EllipsisIcon } from "lucide-vue-next"
import { computed, ref } from "vue"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"

interface Action {
  label: string
  action: string
  disabled?: (row: Record<string, unknown>) => boolean
  loading?: (row: Record<string, unknown>) => boolean
  onClick?: (row: Record<string, unknown>) => void | Promise<void>
}

const isOpen = ref(false)

const props = defineProps<{
  action: Action[]
  row: Record<string, unknown>
}>()

const hasLoadingAction = computed(() =>
  props.action.some((value) => value.loading?.(props.row)),
)

function isDisabled(value: Action) {
  return !!value.disabled?.(props.row) || !!value.loading?.(props.row)
}

async function handleAction(value: Action) {
  if (isDisabled(value) || !value.onClick) return

  try {
    await value.onClick(props.row)
  } finally {
    isOpen.value = false
  }
}
</script>

<template>
  <DropdownMenu v-model:open="isOpen">
    <DropdownMenuTrigger as-child>
      <Button variant="outline" :disabled="hasLoadingAction">
        <EllipsisIcon />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-fit" align="start">
      <DropdownMenuItem
        v-for="value in action"
        :key="value.label"
        :disabled="isDisabled(value)"
        @select.prevent="handleAction(value)"
      >
        <Spinner v-if="value.loading?.(row)" class="mr-2 size-4 animate-spin" />
        {{ value.label }}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
